import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ShieldCheck, Settings2 } from "lucide-react";
import { api, formatApiError, ROLE_LABEL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KIND } from "@/components/legal/LegalParts";
import { MetaForm, TextEditor, VersionsPanel, ReportPanel, STAGE } from "@/components/legal/AdminLegalParts";

const err = (x) => toast.error(formatApiError(x.response?.data?.detail));
const metaOf = (d) => ({ stage: d.stage, required: d.required, audience: d.audience, active: d.active, order: d.order || 0 });

function DocDialog({ doc, onClose, onChanged }) {
  const [meta, setMeta] = useState(metaOf(doc));
  const [full, setFull] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get(`/admin/legal/docs/${doc.id}/versions`).then(({ data }) => setFull({ ...doc, current: data[0] }));
  }, [doc, tick]);
  const saveMeta = async () => { try { await api.put(`/admin/legal/docs/${doc.id}`, meta); toast.success("Ayarlar kaydedildi"); onChanged(); } catch (x) { err(x); } };
  const changed = () => { setTick((t) => t + 1); onChanged(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="legal-doc-dialog">
        <DialogHeader><DialogTitle>{full?.current?.title || doc.draft?.title || doc.code}</DialogTitle></DialogHeader>
        <Tabs defaultValue="text">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="text" data-testid="legal-dlg-tab-text">Metin & yayın</TabsTrigger>
            <TabsTrigger value="meta" data-testid="legal-dlg-tab-meta">Ayarlar</TabsTrigger>
            <TabsTrigger value="versions" data-testid="legal-dlg-tab-versions">Sürüm geçmişi</TabsTrigger>
            <TabsTrigger value="report" data-testid="legal-dlg-tab-report">Onay raporu</TabsTrigger>
          </TabsList>
          <TabsContent value="text" className="pt-3">{full ? <TextEditor key={tick} doc={full} onChanged={changed} /> : "Yükleniyor…"}</TabsContent>
          <TabsContent value="meta" className="pt-3 space-y-4"><MetaForm value={meta} onChange={setMeta} kind={doc.kind} /><Button onClick={saveMeta} className="bg-emerald-700 hover:bg-emerald-800" data-testid="legal-meta-save">Ayarları kaydet</Button></TabsContent>
          <TabsContent value="versions" className="pt-3"><VersionsPanel key={tick} docId={doc.id} /></TabsContent>
          <TabsContent value="report" className="pt-3"><ReportPanel key={tick} docId={doc.id} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ code: "", kind: "AGREEMENT", title: "", summary: "", body: "", stage: "FIRST_LOGIN", required: true, audience: ["BROKER"], active: true, order: 20 });
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/admin/legal/docs", { ...f, required: f.kind === "CONSENT" ? false : f.required }); toast.success("Belge oluşturuldu. Yayımlamak için metni açın."); onCreated(data); onClose(); }
    catch (x) { err(x); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="legal-create-dialog">
        <DialogHeader><DialogTitle>Yeni belge</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Belge kodu *</Label><Input required pattern="[A-Z0-9_]{2,40}" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="ÖR: GIZLILIK" data-testid="legal-create-code" /></div>
            <div><Label>Tür</Label>
              <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}><SelectTrigger data-testid="legal-create-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(KIND).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div><Label>Başlık *</Label><Input required minLength={3} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="legal-create-title" /></div>
          <div><Label>Kısa açıklama</Label><Input value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="legal-create-summary" /></div>
          <div><Label>Metin *</Label><Textarea required minLength={10} rows={8} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="legal-create-body" /></div>
          <MetaForm value={f} onChange={(m) => setF({ ...f, ...m })} kind={f.kind} />
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>İptal</Button><Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" data-testid="legal-create-submit">Taslak olarak oluştur</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminLegalPage() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const [verify, setVerify] = useState(null);
  const load = () => api.get("/admin/legal/docs").then(({ data }) => setDocs(data)).catch(err);
  useEffect(() => { load(); }, []);
  const runVerify = async () => { try { setVerify((await api.get("/admin/legal/verify")).data); } catch (x) { err(x); } };

  return (
    <div className="space-y-6" data-testid="admin-legal-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Hukuki</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Sözleşme ve Belgeler</h1>
          <p className="text-slate-500 mt-1">Belgeleri düzenleyin, yeni sürüm yayımlayın ve onay kayıtlarını inceleyin. Yayımlanmış sürümler ve onay kayıtları değiştirilemez.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runVerify} data-testid="legal-verify-btn"><ShieldCheck className="w-4 h-4 mr-2" />Kayıt bütünlüğünü doğrula</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => setCreating(true)} data-testid="legal-create-btn"><Plus className="w-4 h-4 mr-2" />Yeni belge</Button>
        </div>
      </div>
      {verify && (
        <Card className={`p-4 text-sm ${verify.acceptances.invalid + verify.consents.invalid ? "border-red-300 bg-red-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="legal-verify-result">
          Onay kayıtları: {verify.acceptances.total} kayıt, {verify.acceptances.invalid} geçersiz · İzin kayıtları: {verify.consents.total} kayıt, {verify.consents.invalid} geçersiz
        </Card>
      )}
      <Card className="divide-y">
        {docs.map((d) => (
          <div key={d.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`legal-doc-row-${d.code}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">{d.title}<span className="text-[10px] font-mono text-slate-400">{d.code}</span>
                {d.sample && <Badge variant="outline" className="text-[10px]">Taslak metin</Badge>}
                {d.draft && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800">Yayımlanmamış değişiklik</Badge>}
              </div>
              <div className="text-xs text-slate-500">{KIND[d.kind]} · {STAGE[d.stage]} · {d.audience.map((r) => ROLE_LABEL[r]).join(", ")}</div>
            </div>
            <Badge variant="outline" className={d.required ? "border-red-200 text-red-700" : ""}>{d.required ? "Zorunlu" : "İsteğe bağlı"}</Badge>
            <Badge variant="outline" className={d.active ? "border-emerald-300 text-emerald-800" : "text-slate-500"} data-testid={`legal-doc-active-${d.code}`}>{d.active ? "Aktif" : "Pasif"}</Badge>
            <span className="text-xs font-mono w-10" data-testid={`legal-doc-version-${d.code}`}>{d.current_version ? `v${d.current_version}` : "—"}</span>
            <span className="text-xs text-slate-500 w-28">{d.accept_count_current} onay (güncel)</span>
            <Button size="sm" variant="outline" onClick={() => setOpen(d)} data-testid={`legal-doc-open-${d.code}`}><Settings2 className="w-4 h-4 mr-1.5" />Yönet</Button>
          </div>
        ))}
      </Card>
      {open && <DocDialog doc={open} onClose={() => setOpen(null)} onChanged={load} />}
      <CreateDialog open={creating} onClose={() => setCreating(false)} onCreated={(d) => { load(); setOpen(d); }} />
    </div>
  );
}
